import React from 'react';
import { ComposedChart, XAxis, YAxis, Tooltip, ResponsiveContainer, Bar, Line, Legend, Label } from 'recharts';
import { useTranslation } from 'react-i18next';

// Context.
import { ConsumptionApp } from '../../omaLummeContext';

// Types.
import { GraphTooltipType } from '../../types/consumptionRates';
// Utils.
import { setPriceFormat, roundNumber } from '../../utils/billing';
import { capitalizeFirstLetter } from '../../utils/consumption';

const CustomTooltip: React.FunctionComponent<GraphTooltipType> = ({ active, payload }: GraphTooltipType) => {
  const { t } = useTranslation();

  if (active && payload) {
    const data = payload[0].payload;
    const consumption = data.consumptionAverage || 0;
    const spotPrice = (data.spotPriceAverage || 0) * 100;

    return (
      <div className="tooltip-lumme">
        <div className="tooltip-title">
          {capitalizeFirstLetter(t(`consumption:consumption.reportingGraph-timeAt`))} {data.datePart?.toString().padStart(2, '0')}–
          {((data.datePart || 0) + 1)?.toString().padStart(2, '0')}
        </div>

        <div>
          <span>{t(`consumption:weightedAvg.graphTooltipConsumption`)}</span>
          <span>{setPriceFormat(roundNumber(consumption))} kWh</span>
        </div>
        {spotPrice && (
          <div>
            <span>{t(`consumption:weightedAvg.graphTooltipSpot`)}</span>
            <span>
              {setPriceFormat(roundNumber(spotPrice))} {t('consumption:nordPoolGraph.spotUnit')}
            </span>
          </div>
        )}
      </div>
    );
  }
  return null;
};

const WeightedAverageGraph: React.FunctionComponent = () => {
  const { t } = useTranslation();

  const { consumptionAvg } = ConsumptionApp.useState();

  return (
    <div className="weightedAvgGraph">
      <ResponsiveContainer width={'100%'} height={300}>
        <ComposedChart data={consumptionAvg?.averages.datePartAverages} margin={{ top: 20, right: 10, bottom: 0, left: 10 }}>
          <XAxis dataKey="datePart" stroke="#000000" interval={0} tickFormatter={(value) => `${value.toString().padStart(2, '0')}`} />
          <YAxis
            yAxisId={0}
            width={20}
            tick={{ fill: '#666666' }}
            scale="linear"
            interval="preserveStartEnd"
            strokeWidth={1}
            stroke="#c5c5c5"
            tickFormatter={(value) => roundNumber(value, false, 1)}
          >
            <Label value={'kWh'} offset={10} position="top" fill="#000000" />
          </YAxis>
          <YAxis
            yAxisId={2}
            width={40}
            orientation="right"
            label={{ value: t(`consumption:nordPoolGraph.spotUnit`), angle: 90, position: 'right' }}
            strokeWidth={1}
            stroke="#c5c5c5"
            tickFormatter={(value) => roundNumber(value * 100, false, 1)}
          />
          <Tooltip content={<CustomTooltip />} wrapperStyle={{ width: 300 }} />
          <Bar
            name={t('consumption:weightedAvg.graphLegendConsumption')}
            dataKey="consumptionAverage"
            fill="#66921b"
            legendType="circle"
            radius={[4, 4, 0, 0]}
          />
          <Line
            yAxisId={2}
            type="monotone"
            dot={false}
            dataKey="spotPriceAverage"
            name={t('consumption:weightedAvg.graphLegendSpot')}
            stroke="#004107"
            strokeWidth={3}
          />
          <Legend verticalAlign="bottom" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default WeightedAverageGraph;
